import { useEffect, useState } from 'react';
import { useCustomer } from '../../../lib/customer';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { PortalContainer } from '../PortalShell';

type Order = {
  id: string;
  order_number: string;
  status: string;
  payment_status: string;
  currency: string;
  total: number;
  created_at: string;
  estimated_delivery: string | null;
  tracking_number: string | null;
};

export function PortalPedidos() {
  const { customer } = useCustomer();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customer) return;
    supabase
      .from('orders')
      .select('id, order_number, status, payment_status, currency, total, created_at, estimated_delivery, tracking_number')
      .eq('customer_id', customer.id)
      .order('created_at', { ascending: false })
      .limit(100)
      .then(({ data }) => {
        setOrders((data as Order[]) ?? []);
        setLoading(false);
      });
  }, [customer]);

  if (!customer) return null;

  return (
    <PortalContainer title="Pedidos" eyebrow="Historial completo">
      {loading ? (
        <div style={{ padding: 20, color: theme.textMuted }}>Cargando…</div>
      ) : orders.length === 0 ? (
        <div style={{
          padding: 40,
          background: theme.bg,
          border: `1px dashed ${theme.border}`,
          textAlign: 'center',
          color: theme.textMuted,
        }}>
          Aún no tienes pedidos registrados. Cuando confirmemos tu primera cotización aparecerá aquí.
        </div>
      ) : (
        <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 720 }}>
            <thead>
              <tr style={{ background: theme.bgSoft }}>
                <Th>Folio</Th>
                <Th style={{ textAlign: 'right' }}>Total</Th>
                <Th>Estado</Th>
                <Th>Pago</Th>
                <Th>Fecha</Th>
                <Th>Entrega</Th>
                <Th>{' '}</Th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <Td><span style={{ fontFamily: theme.font.mono, color: theme.textMuted }}>{o.order_number}</span></Td>
                  <Td style={{ textAlign: 'right', fontWeight: 700 }}>
                    ${Number(o.total).toLocaleString('es-MX', { maximumFractionDigits: 0 })} {o.currency}
                  </Td>
                  <Td><Badge>{o.status}</Badge></Td>
                  <Td><Badge color={o.payment_status === 'paid' ? theme.accentDark : theme.warn}>{o.payment_status}</Badge></Td>
                  <Td style={{ color: theme.textMuted, fontSize: 12 }}>
                    {new Date(o.created_at).toLocaleDateString('es-MX')}
                  </Td>
                  <Td style={{ color: theme.textMuted, fontSize: 12 }}>
                    {o.estimated_delivery ? new Date(o.estimated_delivery).toLocaleDateString('es-MX') : '—'}
                  </Td>
                  <Td>
                    <button style={{
                      background: 'transparent',
                      border: 'none',
                      color: theme.accentDark,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      fontFamily: theme.font.sans,
                    }}>
                      Reordenar
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PortalContainer>
  );
}

function Th({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th style={{
      textAlign: 'left',
      padding: '12px 14px',
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: theme.textMuted,
      borderBottom: `1px solid ${theme.border}`,
      ...style,
    }}>
      {children}
    </th>
  );
}

function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '12px 14px', verticalAlign: 'middle', ...style }}>{children}</td>;
}

function Badge({ children, color }: { children: React.ReactNode; color?: string }) {
  return (
    <span style={{
      fontSize: 10,
      fontWeight: 700,
      padding: '3px 8px',
      background: theme.bgSoft,
      color: color ?? theme.brand,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      border: `1px solid ${color ?? theme.border}`,
    }}>
      {children}
    </span>
  );
}
